import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import SharedForm from "../../components/forms/SharedForm";
import placementTestFields from "../../config/placementTestFields";
import { API } from "../../shared/api/Api";
import { selectUserInformations } from "../../features/authSlice";
import { showToast } from "../../utils/utils";
import Loader from "../../components/Live/Loader";

const EditPlacementTestPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUserInformations);

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);

  const fields = useMemo(
    () =>
      placementTestFields.map((field) => {
        if (field.name === "course_id") {
          const onlyPlacementCourses =
            formData.type === "pre_enrollment"
              ? courses.filter((course) => Boolean(course.requires_placement_test))
              : courses;
          return {
            ...field,
            options: onlyPlacementCourses.map((course) => ({ value: course.id, label: course.title })),
          };
        }
        return field;
      }),
    [courses, formData.type]
  );

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const { data } = await axios.get(API.Courses.app_courses_all, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (data.status === 200) {
          setCourses(data.data?.items || []);
        }
      } catch (error) {
        showToast("Error", "Failed to load courses.", "error");
      }
    };

    if (user?.token) {
      fetchCourses();
    }
  }, [user?.token]);

  useEffect(() => {
    const fetchPlacementTest = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API.PlacementTests.get}/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });

        if (data.status === 200) {
          const placementTest = data.data || {};
          setFormData({
            title: placementTest.title || "",
            description: placementTest.description || "",
            price: placementTest.price ?? "",
            type: placementTest.type || "pre_enrollment",
            course_id: placementTest.course_id ?? "",
            max_attempts: placementTest.max_attempts ?? "",
            start_date: placementTest.start_date || "",
            duration_minutes: placementTest.duration_minutes ?? "",
            full_marks: placementTest.full_marks ?? placementTest?.exam?.full_marks ?? 100,
            passing_score: placementTest.passing_score ?? placementTest?.exam?.passing_score ?? 50,
            status: placementTest.status || "draft",
          });
          return;
        }

        showToast("Error", "Failed to load placement test details.", "error");
        navigate("/dashboard/placement-tests");
      } catch (error) {
        showToast("Error", error.response?.data?.message || "Failed to load placement test details.", "error");
        navigate("/dashboard/placement-tests");
      } finally {
        setLoading(false);
      }
    };

    if (user?.token && id) {
      fetchPlacementTest();
    }
  }, [id, navigate, user?.token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "type" && value === "standalone") {
        next.course_id = "";
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.type === "pre_enrollment" && !formData.course_id) {
      showToast("Error", "Please select a course for pre-enrollment placement tests.", "error");
      return;
    }
    if (Number(formData.full_marks) <= 0) {
      showToast("Error", "Full marks must be greater than 0.", "error");
      return;
    }
    if (Number(formData.passing_score) < 0 || Number(formData.passing_score) > 100) {
      showToast("Error", "Passing score must be between 0 and 100.", "error");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API.PlacementTests.update}/${id}`, formData, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (data.status === 200) {
        showToast("Success", "Placement test updated successfully.", "success");
        navigate(`/dashboard/placement-tests/${id}`);
        return;
      }

      showToast("Error", data.message || "Failed to update placement test.", "error");
    } catch (error) {
      showToast("Error", error.response?.data?.message || "Failed to update placement test.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Edit Placement Test</h1>
          <button onClick={() => navigate(`/dashboard/placement-tests/${id}`)} className="text-gray-500 hover:underline">
            Cancel
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          <SharedForm
            fields={fields}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={saving}
            hasCourses={formData.type === "pre_enrollment"}
            entity="placement-test"
            formErrors={{}}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default EditPlacementTestPage;
